/**
 * Freshworks Widget Plugin
 * 
 * A Vue plugin that provides a clean API for the Freshworks support widget.
 * Handles dynamic script loading, environment-based configuration, and
 * all widget interactions.
 * 
 * @usage
 * // In main.js:
 * import freshworks from './plugins/freshworks';
 * Vue.use(freshworks);
 * 
 * // In components:
 * this.$freshworks.open();
 * this.$freshworks.close();
 * this.$freshworks.identify({ name: 'John', email: 'john@example.com' });
 */

// Default widget ID (can be overridden via VUE_APP_FD_WIDGET_ID env var)
const DEFAULT_WIDGET_ID = '158000000279';
const WIDGET_BASE_URL = 'https://widget.freshworks.com/widgets';

/**
 * Configuration object for Freshworks widget
 */
const getConfig = () => ({
  widgetId: process.env.VUE_APP_FD_WIDGET_ID || DEFAULT_WIDGET_ID,
  enabled: process.env.VUE_APP_FD_SUPPORT_WIDGET === 'true' || 
           process.env.VUE_APP_FD_SUPPORT_WIDGET === true,
  secret: process.env.VUE_APP_FD_WIDGET_SECRET || null,
});

/**
 * Freshworks Widget Manager
 * Singleton class that manages the Freshworks support widget lifecycle
 */
class FreshworksManager {
  constructor() {
    this._initialized = false;
    this._loading = false;
    this._isOpen = false;
    this._config = getConfig();
    this._onCloseCallbacks = [];
  }

  /**
   * Get current configuration
   * @returns {Object} Current widget configuration
   */
  get config() {
    return { ...this._config };
  }

  /**
   * Check if widget is enabled via environment config
   * @returns {boolean}
   */
  get isEnabled() {
    return this._config.enabled;
  }

  /**
   * Check if widget has been initialized
   * @returns {boolean}
   */
  get isInitialized() {
    return this._initialized;
  }

  /**
   * Check if widget is currently open
   * @returns {boolean}
   */
  get isOpen() {
    return this._isOpen;
  }

  /**
   * Initialize the Freshworks widget
   * Dynamically loads the script and sets up the widget
   * @returns {Promise<boolean>} Resolves to true if initialization successful
   */
  async init() {
    if (this._initialized || this._loading) {
      return this._initialized;
    }

    if (!this._config.enabled) {
      console.log('[Freshworks]: Widget disabled via configuration');
      return false;
    }

    this._loading = true;

    try {
      // Set up the global FreshworksWidget stub if not exists
      this._setupGlobalStub();

      // Set widget settings
      window.fwSettings = {
        widget_id: parseInt(this._config.widgetId, 10)
      };

      // Load the widget script
      await this._loadScript();

      // Hide the default launcher (we use our own UI triggers)
      this._execute('hide', 'launcher');

      this._initialized = true;
      console.log('[Freshworks]: Widget initialized successfully');
      return true;
    } catch (error) {
      console.error('[Freshworks]: Failed to initialize widget', error);
      return false;
    } finally {
      this._loading = false;
    }
  }

  /**
   * Open the widget
   * @param {Object} [identifyData] - Optional user data to pre-fill the form
   * @param {string} [identifyData.name] - User's name
   * @param {string} [identifyData.email] - User's email
   */
  open(identifyData = null) {
    if (!this._ensureInitialized()) return;

    this._execute('open');
    this._isOpen = true;

    if (identifyData) {
      this.identify(identifyData);
    }

    // Set up close button listener
    this._setupCloseButtonListener();
  }

  /**
   * Close the widget
   */
  close() {
    if (!this._ensureInitialized()) return;

    this._execute('close');
    this._isOpen = false;
    this._triggerOnClose();
  }

  /**
   * Toggle widget open/closed state
   * @param {Object} [identifyData] - Optional user data (used when opening)
   * @returns {boolean} New isOpen state
   */
  toggle(identifyData = null) {
    if (this._isOpen) {
      this.close();
    } else {
      this.open(identifyData);
    }
    return this._isOpen;
  }

  /**
   * Identify user in the widget (pre-fill ticket form)
   * @param {Object} userData
   * @param {string} [userData.name] - User's name
   * @param {string} [userData.email] - User's email
   * @param {string} [userData.phone] - User's phone
   */
  identify(userData) {
    if (!this._ensureInitialized()) return;

    const data = {};
    if (userData.name) data.name = userData.name;
    if (userData.email) data.email = userData.email;
    if (userData.phone) data.phone = userData.phone;

    this._execute('identify', 'ticketForm', data);
  }

  /**
   * Authenticate user with JWT (for logged-in user experience)
   * @param {string} token - JWT token
   */
  authenticate(token) {
    if (!this._ensureInitialized()) return;

    this._execute('authenticate', { token });
  }

  /**
   * Show/hide specific widget launcher
   * @param {boolean} visible
   */
  setLauncherVisible(visible) {
    if (!this._ensureInitialized()) return;

    this._execute(visible ? 'show' : 'hide', 'launcher');
  }

  /**
   * Register callback for when widget is closed
   * @param {Function} callback
   * @returns {Function} Unsubscribe function
   */
  onClose(callback) {
    if (typeof callback !== 'function') return () => {};
    
    this._onCloseCallbacks.push(callback);
    
    return () => {
      const idx = this._onCloseCallbacks.indexOf(callback);
      if (idx > -1) this._onCloseCallbacks.splice(idx, 1);
    };
  }

  /**
   * Destroy the widget and clean up
   */
  destroy() {
    if (window.FreshworksWidget) {
      try {
        this._execute('destroy');
      } catch (e) {
        // Widget may not support destroy
      }
    }
    this._initialized = false;
    this._isOpen = false;
    this._onCloseCallbacks = [];
  }

  // ─────────────────────────────────────────────────────────────
  // Private methods
  // ─────────────────────────────────────────────────────────────

  /**
   * Set up the global FreshworksWidget stub function
   * This queues commands until the real script loads
   */
  _setupGlobalStub() {
    if (typeof window.FreshworksWidget === 'function') return;

    const stub = function() {
      stub.q.push(arguments);
    };
    stub.q = [];
    window.FreshworksWidget = stub;
  }

  /**
   * Load the Freshworks widget script
   * @returns {Promise<void>}
   */
  _loadScript() {
    return new Promise((resolve, reject) => {
      // Check if script already exists
      const existingScript = document.querySelector(
        `script[src*="widget.freshworks.com/widgets/${this._config.widgetId}"]`
      );
      
      if (existingScript) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = `${WIDGET_BASE_URL}/${this._config.widgetId}.js`;
      script.async = true;
      script.defer = true;
      script.id = 'freshworks-widget-script';

      script.onload = () => {
        console.log('[Freshworks]: Script loaded successfully');
        resolve();
      };

      script.onerror = (error) => {
        console.error('[Freshworks]: Failed to load script', error);
        reject(new Error('Failed to load Freshworks widget script'));
      };

      document.body.appendChild(script);
    });
  }

  /**
   * Execute a FreshworksWidget command
   * @param {...any} args - Arguments to pass to FreshworksWidget
   */
  _execute(...args) {
    if (typeof window.FreshworksWidget === 'function') {
      window.FreshworksWidget(...args);
    }
  }

  /**
   * Ensure widget is initialized before executing commands
   * @returns {boolean}
   */
  _ensureInitialized() {
    if (!this._initialized) {
      console.warn('[Freshworks]: Widget not initialized. Call init() first.');
      return false;
    }
    return true;
  }

  /**
   * Set up listener for widget close button
   */
  _setupCloseButtonListener() {
    setTimeout(() => {
      try {
        const widgetFrame = document.querySelector('#widget-frame');
        if (!widgetFrame) {
          console.warn('[Freshworks]: Widget frame not found');
          return;
        }

        const iframe = widgetFrame.contentWindow || widgetFrame.contentDocument;
        if (iframe?.document) {
          const closeButton = iframe.document.querySelector('[data-testid=header-close]');
          if (closeButton) {
            closeButton.addEventListener('click', () => {
              this._isOpen = false;
              this._triggerOnClose();
            });
          }
        }
      } catch (e) {
        // Cross-origin errors are expected in some configurations
        console.warn('[Freshworks]: Could not attach close button listener', e);
      }
    }, 1000);
  }

  /**
   * Trigger all registered onClose callbacks
   */
  _triggerOnClose() {
    this._onCloseCallbacks.forEach(cb => {
      try {
        cb();
      } catch (e) {
        console.error('[Freshworks]: Error in onClose callback', e);
      }
    });
  }
}

// Create singleton instance
const freshworksManager = new FreshworksManager();

/**
 * Vue Plugin
 */
export default {
  install(Vue) {
    // Expose the manager instance on Vue prototype
    Vue.prototype.$freshworks = freshworksManager;

    // Auto-initialize if enabled
    if (freshworksManager.isEnabled) {
      // Use nextTick to ensure Vue is fully initialized
      Vue.nextTick(() => {
        freshworksManager.init().catch(err => {
          console.error('[Freshworks]: Auto-initialization failed', err);
        });
      });
    }
  }
};

// Also export the manager for direct usage if needed
export { freshworksManager, getConfig, DEFAULT_WIDGET_ID };
